import { type Handoff, isHandoffPending } from '../lib/synergyR11';
import { Icon } from './Icon';

/**
 * S8 floor-handoff card (R11 #5 — showcase-conversation-synergy.md §6 #5, §3 S8; client-scenarios.md
 * S2 beat 5). The visitor asked to be walked to a piece that is physically on the stand. The server
 * side is already live (`handoffs` table + /staff view); this card closes the loop on the showcase:
 *   - `requested` → "Finding a colleague for you…" (a colleague is being paged; spinner copy).
 *   - `assigned` / `arriving` → "{staffName} is on the way ✓".
 *
 * It sits as a persistent-but-unobtrusive banner within the scene container (design-language.md §8
 * "container ready for … handoff"), ABOVE the active scene — browse / order-review keep working
 * underneath. It stays until the visitor dismisses it (touch is a full peer) or the session resets.
 *
 * Design-language: calm surface + hairline + brass, no error styling. Tokens only, day+night aware,
 * `.reveal` fade+rise (reduced-motion safe via the motion tokens).
 */
export function HandoffCard({ handoff, onDismiss }: { handoff: Handoff; onDismiss: () => void }) {
  const pending = isHandoffPending(handoff);
  const name = handoff.staffName?.trim() || 'A colleague';

  return (
    <div
      data-testid="handoff-card"
      className="reveal flex items-center gap-3 rounded-xl bg-surface ring-1 ring-line px-4 py-3 shadow-elev-lift"
      aria-live="polite"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brass-soft ring-1 ring-brass/30">
        <Icon name={pending ? 'users' : 'check'} size={16} className="text-brass" />
      </span>
      <div className="min-w-0 flex-1">
        {pending ? (
          <p className="text-sm text-ink leading-snug" data-testid="handoff-status-pending">
            Finding a colleague for you
            <span aria-hidden className="handoff-dots" />
          </p>
        ) : (
          <p className="text-sm text-ink leading-snug" data-testid="handoff-status-assigned">
            <span className="font-medium">{name}</span> is on the way
          </p>
        )}
        <p className="text-[11px] text-subtle leading-snug">
          {pending
            ? "They'll walk you to the piece on the stand."
            : 'Meet them at the piece on the stand — no rush.'}
        </p>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="handoff-dismiss"
        onClick={onDismiss}
        className="shrink-0 rounded-full p-1.5 text-faint hover:text-ink hover:bg-fill transition-colors"
      >
        <Icon name="x" size={15} />
      </button>
    </div>
  );
}
